/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import tw from 'twin.macro';

import { Ripple } from '@rmwc/ripple';
import { ReactComponent as FilterToolSvg } from 'assets/Icons/filter-tool.svg';
import { ReactComponent as FilterOffSvg } from 'assets/Icons/filter_off.svg';
import { FilterWrapper } from 'components/shared/AdvancedFilterMenu/FilterWrapper';
import { Hint } from 'components/shared/Hint';
import { hoverDropShadow } from 'components/shared/twin.styles';
import { isEmpty, keys } from 'lodash';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useClickAway, useToggle } from 'react-use';
import { useHistory } from 'react-router-dom';
import { CollectionService } from 'services/collection.service';
import {
  applyCollectionFilters,
  CollectionFilters,
  collectionSelector,
  resetCollectionFilters,
  setPartialCollectionFilters,
  setSelectedCollectionId,
  setTagFilters,
} from 'slices/collection.slice';
import { CollectionType, FilterLayer } from 'utils/enum';
import { customToast } from 'utils/toast.util';
import { RootState } from 'reducers';
import { extract } from 'utils/generic.util';
import { isCollectionPage } from 'components/VideoPlayer/Transcription/MediaUtilities';
import { changePage } from 'slices/pagination.slice';
import { toggleReloadLibrary } from 'slices/toggle.slice';
import { setSelectMode, setSelectedRows } from 'actions';

type Props = {};

export const CollectionFilterToolbar = (props: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const filterToolbarRef = useRef<HTMLDivElement>(null);

  const collectionSlice = useSelector(collectionSelector);
  const collection = useSelector((state: RootState) => state.collection);

  useClickAway(filterToolbarRef, () => {
    if (!show) return;

    const timer = setTimeout(() => {
      toggleShow(false);
      clearTimeout(timer);
    }, 200);
  });

  const [show, toggleShow] = useToggle(false);

  const handleClickShow = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();

    toggleShow();
  };

  /** Leave collection page for Library; keep filters.collections / collectionIds. */
  const navigateToLibraryKeepingCollectionFilters = () => {
    dispatch(setSelectedCollectionId(''));
    dispatch(setSelectedRows([]));
    dispatch(setSelectMode(false));
    dispatch(
      changePage({
        page_number: 1,
        page_size: 30,
      }),
    );
    history.push('/library');
    dispatch(toggleReloadLibrary());
  };

  const handleApplyFilters = async (filters: Partial<CollectionFilters>) => {
    const isEmptyFilters = keys(filters).every((key) =>
      isEmpty(filters[key as keyof CollectionFilters]),
    );

    if (isEmptyFilters && !collectionSlice.hasFilters) return;

    toggleShow(false);

    const filterAsync = CollectionService.getCollectionsWithFilters({
      orgIds: filters?.orgIds ?? [],
      tagIds: filters?.tagIds ?? [],
    });

    customToast.promise(filterAsync, {
      loading: 'Applying filter(s)...',
      success: 'Done',
      error: 'Something went wrong. Please try again later!',
    });

    try {
      const { data: response } = await filterAsync;

      if (isEmpty(response.collections)) {
        customToast.warning(
          <div>
            <div>
              There are no collections associated to this filter conditions.
            </div>
            <div>Please check and try again.</div>
          </div>,
        );

        return;
      }

      const matchItems = response.collections.map((item) => ({
        id: item.collection_id,
        name: item.collection_name,
        type: CollectionType.STANDARD,
      }));
      const collectionIds = matchItems.map((item) => item.id);
      const matchCollectionIds = extract(response.collections, 'collection_id');
      const isCurrentCollectionInTag = matchCollectionIds.includes(
        collection.selectedId as string,
      );

      const shouldReturnToLibrary =
        isCollectionPage() && !isCurrentCollectionInTag;

      if (shouldReturnToLibrary) {
        dispatch(setSelectedCollectionId(''));
      }

      dispatch(
        setPartialCollectionFilters({
          orgIds: filters?.orgIds ?? [],
          tagIds: filters?.tagIds ?? [],
          collections: matchItems,
          collectionIds,
        }),
      );
      dispatch(
        setTagFilters({
          collections: matchItems,
          collectionIds,
        }),
      );
      dispatch(applyCollectionFilters());

      // If the collection is not in the tag, return to library
      if (shouldReturnToLibrary) {
        navigateToLibraryKeepingCollectionFilters();
        return;
      }

      // Library, or collection in tag → stay and run Library search with new pool.
      dispatch(
        changePage({
          page_number: 1,
          page_size: 30,
        }),
      );
      dispatch(toggleReloadLibrary());
    } catch (error) {}
  };

  const handleClearFilters = () => {
    const selectedCollection =
      collection.items.find((item) => item.id === collection.selectedId) ??
      collection.visibleCollection.find(
        (item) => item.id === collection.selectedId,
      );

    // Without Archive tag filter, archived collections are hidden — leave the page.
    const shouldReturnToLibrary =
      isCollectionPage() && Boolean(selectedCollection?.isArchived);

    dispatch(resetCollectionFilters());

    toggleShow(false);

    if (shouldReturnToLibrary) {
      navigateToLibraryKeepingCollectionFilters();
      return;
    }

    dispatch(toggleReloadLibrary());
  };

  return (
    <div ref={filterToolbarRef}>
      <FilterWrapper
        menuIsOpen={show}
        filterLayers={[FilterLayer.TAG, FilterLayer.ORG]}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        selectionFilters={collection.filters}
        showReset={collectionSlice.hasFilters}
        anchorElement={
          <Hint text="Collection filters" enterDelay={200}>
            <Ripple onClick={handleClickShow}>
              <span tw="flex rounded-lg">
                {collectionSlice.hasFilters ? (
                  <FilterOffSvg tw="fill[#E7483D]" css={[iconCss]} />
                ) : (
                  <FilterToolSvg tw="fill[#7f8090]" css={[iconCss]} />
                )}
              </span>
            </Ripple>
          </Hint>
        }
      />
    </div>
  );
};

const iconCss = css`
  ${hoverDropShadow(0.15)}
  ${tw`cursor-pointer h-[2.7rem] w-[2.7rem] mt-[-2px]`}
`;
